/**
 * Convert whatever the browser recorded into 16 kHz mono WAV.
 *
 * MediaRecorder gives you the codec the browser prefers — Chrome picks WebM/Opus — and
 * WebM is not among the container formats speech APIs accept. Gemini's list is WAV, MP3,
 * AIFF, AAC, OGG and FLAC; handed a WebM it does not error, it returns "[inaudible]" or,
 * worse, something plausible assembled from the prompt. A transcription pipeline that fails
 * by inventing text is far more dangerous than one that fails loudly.
 *
 * Converting here rather than server-side also means the browser and the recording device
 * send byte-identical formats, so the two paths cannot drift, and 16 kHz mono is what every
 * speech model wants anyway — anything more is bytes paid for and thrown away.
 */

/** What speech models are trained on; higher rates are wasted upload. */
const TARGET_SAMPLE_RATE = 16_000;

export interface ConvertedAudio {
  readonly blob: Blob;
  readonly durationSec: number;
}

export async function toWav16kMono(input: Blob): Promise<ConvertedAudio> {
  const bytes = await input.arrayBuffer();
  if (bytes.byteLength === 0) throw new Error('The recording was empty');

  // A plain AudioContext is only needed to decode; the resample happens offline.
  const decodeContext = new AudioContext();
  let decoded: AudioBuffer;
  try {
    decoded = await decodeContext.decodeAudioData(bytes.slice(0));
  } catch (err) {
    throw new Error(
      `This browser recorded audio in a format it cannot decode (${input.type || 'unknown'}). ` +
        `Try a different browser, or type what happened instead.`,
    );
  } finally {
    void decodeContext.close();
  }

  if (decoded.duration <= 0) throw new Error('The recording contained no audio');

  // Rendering through a single-channel context at the target rate does the downmix and the
  // resample together, using the browser's own resampler rather than a hand-rolled one.
  const frames = Math.max(1, Math.ceil(decoded.duration * TARGET_SAMPLE_RATE));
  const offline = new OfflineAudioContext(1, frames, TARGET_SAMPLE_RATE);
  const source = offline.createBufferSource();
  source.buffer = decoded;
  source.connect(offline.destination);
  source.start();

  const rendered = await offline.startRendering();
  const samples = rendered.getChannelData(0);

  return {
    blob: new Blob([encodeWav(samples, TARGET_SAMPLE_RATE)], { type: 'audio/wav' }),
    durationSec: decoded.duration,
  };
}

/** Minimal 16-bit PCM WAV. */
function encodeWav(samples: Float32Array, sampleRate: number): ArrayBuffer {
  const bytesPerSample = 2;
  const buffer = new ArrayBuffer(44 + samples.length * bytesPerSample);
  const view = new DataView(buffer);

  const ascii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i += 1) view.setUint8(offset + i, text.charCodeAt(i));
  };

  ascii(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * bytesPerSample, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true); // PCM header size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * bytesPerSample, true); // byte rate
  view.setUint16(32, bytesPerSample, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  ascii(36, 'data');
  view.setUint32(40, samples.length * bytesPerSample, true);

  let offset = 44;
  for (const sample of samples) {
    // Clamp before scaling; a value outside [-1, 1] would wrap and become a loud click.
    const clamped = Math.max(-1, Math.min(1, sample));
    view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
    offset += bytesPerSample;
  }
  return buffer;
}
